import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import AIDemoPanel from '../components/AIDemoPanel';
import LoginSuccessScreen from '../components/LoginSuccessScreen';

const TAB_CFG = {
  user: {
    heading: 'Welcome back',
    sub: 'Sign in to your workspace',
    btn: 'Sign in',
    btnGrad: 'linear-gradient(135deg, #0A2E17 0%, #1B5E3B 28%, #2E7D52 52%, #1B5E3B 76%, #0A2E17 100%)',
    btnShadow: '0 4px 20px rgba(27,94,59,0.45), inset 0 1px 0 rgba(255,255,255,0.12)',
    activeStyle: { background: 'linear-gradient(135deg, #0A2E17 0%, #1B5E3B 28%, #2E7D52 52%, #1B5E3B 76%, #0A2E17 100%)', color: '#fff', boxShadow: '0 2px 12px rgba(27,94,59,0.5)' },
    hint: { label: 'Demo:', value: 'jay@gmail.com / admin123', color: '#4CAF7D' }
  },
  admin: {
    heading: 'Admin Access',
    sub: 'Sign in to the admin panel',
    btn: 'Access Admin Panel',
    btnGrad: 'linear-gradient(135deg, #b45309, #f59e0b)',
    btnShadow: '0 4px 20px rgba(245,158,11,0.4)',
    activeStyle: { background: 'linear-gradient(135deg, #b45309, #f59e0b)', color: '#fff', boxShadow: '0 2px 12px rgba(245,158,11,0.45)' },
    hint: { label: 'Admin demo:', value: 'jay@gmail.com / admin123', color: '#f59e0b' }
  },
  register: {
    heading: 'Create account',
    sub: 'Join the ERP Intelligence Platform',
    btn: 'Create account',
    btnGrad: 'linear-gradient(135deg, #059669, #10b981)',
    btnShadow: '0 4px 20px rgba(16,185,129,0.4)',
    activeStyle: { background: 'linear-gradient(135deg, #059669, #10b981)', color: '#fff', boxShadow: '0 2px 12px rgba(16,185,129,0.45)' },
    hint: null
  }
};

export default function Login() {
  const [tab, setTab]                     = useState('user');
  const [name, setName]                   = useState('');
  const [email, setEmail]                 = useState('');
  const [password, setPassword]           = useState('');
  const [confirmPw, setConfirmPw]         = useState('');
  const [loading, setLoading]             = useState(false);
  const [error, setError]                 = useState('');
  const [shake, setShake]                 = useState(false);
  const [success, setSuccess]             = useState(false);
  const [regSuccess, setRegSuccess]       = useState(false);
  const [mounted, setMounted]             = useState(false);

  const { login, register, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate(user.role === 'admin' ? '/admin' : '/', { replace: true });
  }, []); // eslint-disable-line

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 80);
    return () => clearTimeout(t);
  }, []);

  const switchTab = (t) => {
    setTab(t);
    setError('');
    setPassword('');
    setConfirmPw('');
    setName('');
  };

  const triggerShake = (msg) => {
    setError(msg);
    setShake(true);
    setTimeout(() => setShake(false), 600);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (tab === 'register') {
      if (password.length < 6) return triggerShake('Password must be at least 6 characters.');
      if (password !== confirmPw) return triggerShake('Passwords do not match.');
      setLoading(true);
      try {
        await register(name, email, password);
        setRegSuccess(true);
        setTimeout(() => { setRegSuccess(false); switchTab('user'); }, 2200);
      } catch (err) {
        triggerShake(err.response?.data?.error || 'Registration failed. Please try again.');
      } finally {
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    try {
      const data = await login(email, password);
      if (tab === 'admin') {
        if (data.user?.role !== 'admin') {
          setLoading(false);
          return triggerShake('This account does not have admin privileges.');
        }
        setSuccess(true);
        setTimeout(() => navigate('/admin'), 3200);
      } else {
        setSuccess(true);
        setTimeout(() => navigate('/'), 3200);
      }
    } catch (err) {
      triggerShake(err.response?.data?.error || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const cfg = TAB_CFG[tab];

  return (
    <div style={S.page}>
      <div style={S.bgGradient} />
      {/* Watercolor brushstroke layers */}
      <div style={S.brush1} />
      <div style={S.brush2} />
      <div style={S.brush3} />
      {/* Grain / noise overlay for texture */}
      <div style={S.noise} />
      <div style={{ ...S.orb, width: 500, height: 500, top: '-8%', left: '-8%', animation: 'orbFloat 10s ease-in-out infinite' }} />
      <div style={{ ...S.orb, width: 350, height: 350, bottom: '-5%', right: '-5%', animation: 'orbFloat 13s ease-in-out infinite reverse', opacity: 0.5 }} />
      <div style={{ ...S.orb, width: 200, height: 200, top: '45%', right: '18%', animation: 'orbFloat 8s ease-in-out 2s infinite', opacity: 0.35 }} />
      <div style={S.grid} />

      {/* Split layout */}
      <div style={S.split}>
        <div style={S.leftCol} className="demo-col">
          <AIDemoPanel />
        </div>
        <div style={S.rightCol} className="login-col">

      {/* Card */}
      <div style={{
        ...S.card,
        opacity: mounted ? 1 : 0,
        transform: mounted ? 'translateY(0) scale(1)' : 'translateY(40px) scale(0.97)',
        transition: 'opacity 0.55s cubic-bezier(0.175,0.885,0.32,1.05), transform 0.55s cubic-bezier(0.175,0.885,0.32,1.05)'
      }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 20, animation: 'fadeInUp 0.5s ease 0.2s both' }}>
          <img
            src="/logo.svg"
            alt="SolutionOne"
            style={{ width: 108, height: 'auto', marginBottom: 6,
              filter: 'drop-shadow(0 4px 14px rgba(27,94,59,0.30))' }}
          />
          <div style={S.logoSub}>ERP Intelligence Platform</div>
        </div>

        {/* Tab switcher */}
        <div style={S.tabBar}>
          {['user', 'admin', 'register'].map(t => (
            <button key={t} type="button" onClick={() => switchTab(t)} style={{
              ...S.tabBtn,
              ...(tab === t ? TAB_CFG[t].activeStyle : {})
            }}>
              {t === 'user' ? '👤 User' : t === 'admin' ? '🔐 Admin' : '✏️ Register'}
            </button>
          ))}
        </div>

        {/* Heading */}
        <div style={{ marginBottom: 20, animation: 'fadeInUp 0.4s ease 0.1s both' }}>
          <h2 style={S.heading}>{cfg.heading}</h2>
          <p style={S.subheading}>{cfg.sub}</p>
        </div>

        {/* Error */}
        {error && (() => {
          const isPending = error.toLowerCase().includes('awaiting admin approval') || error.toLowerCase().includes('pending');
          const boxStyle = isPending
            ? { background: 'rgba(217,119,6,0.12)', border: '1px solid rgba(217,119,6,0.4)', color: '#fcd34d' }
            : S.errorBox;
          const iconColor = isPending ? '#f59e0b' : '#dc2626';
          return (
            <div style={{ ...S.alertBox, ...boxStyle }} className={shake ? 'animate-shake' : ''}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2.5">
                {isPending
                  ? <><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><circle cx="12" cy="16" r="1" fill={iconColor}/></>
                  : <><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></>
                }
              </svg>
              {error}
            </div>
          );
        })()}

        {/* Register success */}
        {regSuccess && (
          <div style={{ ...S.alertBox, background: 'rgba(217,119,6,0.12)', border: '1px solid rgba(217,119,6,0.4)', color: '#fcd34d' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2.5">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><circle cx="12" cy="16" r="1" fill="#f59e0b"/>
            </svg>
            Account created! Awaiting admin approval before you can sign in.
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} style={S.form}>
          {tab === 'register' && (
            <div style={S.field}>
              <label style={S.label}>Full name</label>
              <input type="text" value={name} onChange={e => setName(e.target.value)}
                className="fancy-input" style={S.input} placeholder="Jane Smith" required />
            </div>
          )}

          <div style={S.field}>
            <label style={S.label}>Email address</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)}
              className="fancy-input" style={S.input} placeholder="you@example.com" required />
          </div>

          <div style={S.field}>
            <label style={S.label}>Password</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)}
              className="fancy-input" style={S.input} placeholder="••••••••" required />
          </div>

          {tab === 'register' && (
            <div style={S.field}>
              <label style={S.label}>Confirm password</label>
              <input type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)}
                className="fancy-input" style={S.input} placeholder="••••••••" required />
            </div>
          )}

          <button type="submit" disabled={loading} className="btn-lift" style={{
            ...S.btn, background: cfg.btnGrad, boxShadow: cfg.btnShadow,
            opacity: loading ? 0.82 : 1, marginTop: 4
          }}>
            {loading ? (
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
                <span style={S.spinner} />
                {tab === 'register' ? 'Creating account…' : 'Signing in…'}
              </span>
            ) : cfg.btn}
          </button>
        </form>

        {/* Hint */}
        {cfg.hint && (
          <div style={{ textAlign: 'center', marginTop: 20, animation: 'fadeIn 0.5s ease 0.7s both' }}>
            <span style={{ fontSize: 12, color: '#9b9b9b' }}>{cfg.hint.label} </span>
            <span style={{ fontSize: 12, color: cfg.hint.color, fontWeight: 500 }}>{cfg.hint.value}</span>
          </div>
        )}
      </div>
        </div>{/* /rightCol */}
      </div>{/* /split */}

      {/* Login success screen */}
      {success && <LoginSuccessScreen isAdmin={tab === 'admin'} />}

      <style>{`
        @keyframes orbFloat {
          0%,100%{ transform: translate(0,0) scale(1) }
          33%    { transform: translate(30px,-25px) scale(1.08) }
          66%    { transform: translate(-20px,15px) scale(0.93) }
        }
        @media (max-width: 900px) {
          .demo-col  { display: none !important; }
          .login-col { flex: 0 0 100% !important; }
        }
      `}</style>
    </div>
  );
}

const S = {
  page: {
    minHeight: '100vh', display: 'flex', alignItems: 'stretch',
    position: 'relative', overflow: 'hidden',
    background: 'linear-gradient(160deg, #020D05 0%, #041208 12%, #071E0C 25%, #0A2E17 42%, #071808 58%, #0A2C14 72%, #051008 88%, #020D05 100%)'
  },
  split:   { display: 'flex', width: '100%', position: 'relative', zIndex: 2 },
  leftCol: {
    flex: '0 0 50%', minHeight: '100vh',
    borderRight: '1px solid rgba(255,255,255,0.06)',
    background: 'radial-gradient(ellipse at 50% 40%, rgba(27,94,59,0.12) 0%, transparent 70%)',
  },
  rightCol: {
    flex: '0 0 50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '32px 24px', minHeight: '100vh', boxSizing: 'border-box',
  },
  bgGradient: {
    position: 'absolute', inset: 0, pointerEvents: 'none',
    background: [
      'radial-gradient(ellipse at 20% 30%, rgba(27,94,59,0.45) 0%, transparent 45%)',
      'radial-gradient(ellipse at 75% 65%, rgba(10,46,23,0.6) 0%, transparent 50%)',
      'radial-gradient(ellipse at 50% 90%, rgba(46,125,82,0.2) 0%, transparent 40%)',
      'radial-gradient(ellipse at 85% 15%, rgba(15,61,34,0.35) 0%, transparent 40%)',
    ].join(', ')
  },
  /* Watercolor brushstroke overlays — applied as inline divs in JSX */
  orb: {
    position: 'absolute', borderRadius: '50%', pointerEvents: 'none',
    background: 'radial-gradient(circle, rgba(27,94,59,0.35) 0%, transparent 70%)'
  },
  /* Horizontal brushstroke shapes */
  brush1: {
    position: 'absolute', width: '130%', height: 70, left: '-15%', top: '18%',
    borderRadius: '50%', background: 'rgba(20,80,45,0.18)',
    filter: 'blur(28px)', transform: 'rotate(-2deg)', pointerEvents: 'none'
  },
  brush2: {
    position: 'absolute', width: '90%', height: 50, right: '-10%', top: '40%',
    borderRadius: '50%', background: 'rgba(27,94,59,0.14)',
    filter: 'blur(22px)', transform: 'rotate(1.5deg)', pointerEvents: 'none'
  },
  brush3: {
    position: 'absolute', width: '110%', height: 60, left: '-5%', bottom: '22%',
    borderRadius: '50%', background: 'rgba(46,125,82,0.12)',
    filter: 'blur(25px)', transform: 'rotate(-1deg)', pointerEvents: 'none'
  },
  noise: {
    position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0, opacity: 0.035,
    backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/%3E%3C/svg%3E")',
    backgroundRepeat: 'repeat', backgroundSize: '256px 256px'
  },
  grid: {
    position: 'absolute', inset: 0, pointerEvents: 'none',
    backgroundImage: 'linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)',
    backgroundSize: '40px 40px'
  },
  card: {
    position: 'relative', zIndex: 2, width: 430,
    background: 'rgba(255,255,255,0.07)',
    backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)',
    border: '1px solid rgba(255,255,255,0.12)', borderRadius: 22,
    padding: '36px 38px',
    boxShadow: '0 24px 80px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.1)'
  },
  logoSub: { fontSize: 12, color: '#4CAF7D', letterSpacing: '0.3px' },
  tabBar: {
    display: 'flex', gap: 5, padding: 4,
    background: 'rgba(0,0,0,0.3)', borderRadius: 13, marginBottom: 24
  },
  tabBtn: {
    flex: 1, padding: '8px 4px', borderRadius: 10, border: 'none',
    background: 'transparent', color: 'rgba(255,255,255,0.45)',
    fontSize: 12, fontWeight: 600, cursor: 'pointer',
    transition: 'all 0.22s ease', letterSpacing: '0.2px'
  },
  heading:    { fontSize: 21, fontWeight: 700, color: '#fff', marginBottom: 4 },
  subheading: { fontSize: 13, color: 'rgba(255,255,255,0.5)', marginBottom: 0 },
  alertBox: {
    display: 'flex', alignItems: 'center', gap: 8,
    borderRadius: 10, padding: '10px 14px', fontSize: 13, marginBottom: 14
  },
  errorBox: {
    background: 'rgba(220,38,38,0.15)', border: '1px solid rgba(220,38,38,0.35)', color: '#fca5a5'
  },
  form:  { display: 'flex', flexDirection: 'column', gap: 14 },
  field: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontSize: 13, fontWeight: 500, color: 'rgba(255,255,255,0.75)' },
  input: {
    background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: 10, padding: '11px 14px', fontSize: 14, color: '#fff', width: '100%'
  },
  btn: {
    color: '#fff', border: 'none', borderRadius: 11,
    padding: '13px', fontSize: 14, fontWeight: 600, width: '100%'
  },
  spinner: {
    width: 16, height: 16, borderRadius: '50%',
    border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff',
    display: 'inline-block', animation: 'spin 0.7s linear infinite'
  },
  successOverlay: {
    position: 'fixed', inset: 0, zIndex: 9999,
    background: 'linear-gradient(135deg, #059669, #10b981)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    animation: 'successOverlay 1.5s ease forwards'
  }
};
